import { Grid } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import BackButton from 'app/components/BackButton'
import { LocaleSwitcher } from 'app/components/LocaleSwitcher'
import { useSharedContext } from 'app/contexts/shared'
import useShowBackButton from 'app/hooks/useShowBackButton'
import FigLogo from 'app/images/header/fig-logo.svg'
import KizzoLogo from 'app/images/header/kizzo-logo.png'
import { OFFER_ROUTES, PARTNER_ID_MAPPING } from 'app/utils/constants'

const FIG_NAV_LOGO_URL = 'https://fig.ca/logos/fig.svg'
const FIG_ELITE_NAV_LOGO_URL = 'https://fig.ca/logos/fig-elite.svg'
const FIG_PARTNER_NAME = 'Fig Financial'

const FIG_FINANCIAL_PARTNER_IDS = new Set([PARTNER_ID_MAPPING.production.FIG_FINANCIAL, PARTNER_ID_MAPPING.staging.FIG_FINANCIAL])

function shouldUseCanonicalFigNavLogo(partnerId: string | undefined, name: string | undefined): boolean {
    const nameOk = (name ?? '').trim().toLowerCase() === FIG_PARTNER_NAME.toLowerCase()
    const idOk = !!partnerId && FIG_FINANCIAL_PARTNER_IDS.has(partnerId)
    return nameOk || idOk
}

/**
 * Header — back, logo, locale. FIG_TO_KIZZO / KIZZO_TO_FIG: pathname drives logo; Fig Financial uses fig.ca SVGs (no Fairstone Q2).
 */
function Header() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const showBackButton = useShowBackButton()
    const { partnerConfig, isKizzoBranded, isSuperPrimeOffer, offerData } = useSharedContext()
    const canonicalFigNav = shouldUseCanonicalFigNavLogo(partnerConfig?.partner_id, partnerConfig?.name)

    const theme = useTheme()

    // Offer pages: show prequal partner logo - pathname is source of truth
    const showFigLogoOnFigToKizzoOfferPage = location.pathname.includes(`/${OFFER_ROUTES.FIG_TO_KIZZO_OFFER}`)
    const showKizzoLogoOnKizzoToFigOfferPage = location.pathname.includes(`/${OFFER_ROUTES.KIZZO_TO_FIG_OFFER}`)
    // KIZZO_TO_FIG origination: show Fig logo when past the offer page (safeguard for offerData timing)
    const showFigLogoForKizzoToFigOrigination = offerData?.data?.offer_type === 'KIZZO_TO_FIG' && !showKizzoLogoOnKizzoToFigOfferPage

    let logoSrc: string
    let logoAlt: string
    if (showFigLogoOnFigToKizzoOfferPage || showFigLogoForKizzoToFigOrigination) {
        logoSrc = FigLogo
        logoAlt = t('common.alt.figLogo')
    } else if (showKizzoLogoOnKizzoToFigOfferPage) {
        logoSrc = KizzoLogo
        logoAlt = t('common.alt.kizzoLogo')
    } else if (isKizzoBranded) {
        logoSrc = KizzoLogo
        logoAlt = t('common.alt.kizzoLogo')
    } else if (canonicalFigNav) {
        logoSrc = isSuperPrimeOffer ? FIG_ELITE_NAV_LOGO_URL : FIG_NAV_LOGO_URL
        logoAlt = isSuperPrimeOffer ? t('common.alt.figEliteLogo') : t('common.alt.figLogo')
    } else {
        logoSrc = partnerConfig?.logo || FigLogo
        logoAlt =
            partnerConfig?.logo && (partnerConfig.name ?? '').trim() !== ''
                ? t('common.alt.partnerLogo', { name: partnerConfig.name.trim() })
                : t('common.alt.figLogo')
    }

    return (
        <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            data-testid="header"
            sx={{
                padding: '14px 24px',
                backgroundColor: theme.color.white
            }}
        >
            {showBackButton && (
                <Grid sx={{ display: { xs: 'block', sm: 'none' } }}>
                    <BackButton onClick={() => navigate(-1)} />
                </Grid>
            )}
            <img
                src={logoSrc}
                alt={logoAlt}
                style={{
                    maxHeight: 32,
                    maxWidth: canonicalFigNav ? 'min(200px, 55vw)' : undefined,
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    flexShrink: 1
                }}
            />
            <Grid sx={{ display: 'flex', alignItems: 'center' }}>
                <LocaleSwitcher />
            </Grid>
        </Grid>
    )
}

export default Header
